import classNames from 'classnames';
import moment from 'moment';
import React from 'react';

import { DbKey } from '../../../../adl-gen/common/db';
import { I18nContent } from '../../../../adl-gen/totalinteraction/futurefit/db';
import { TranslateFn } from '../../../../internationalization/i18n-hoc';
import { TableCell } from '../../../../ui/widgets/table/table';
import { assertNever } from '../../../../util/types';
import { ItemState, ListEntry, ListFieldInfo, ToggleValue } from './list-types';
import { Instant } from '../../../../adl-gen/common';
import { CheckItem } from '../../../../ui/widgets/check-item/check-item';
import { TabularCheckItemObserver, TabularTextInputObserver, MultiSelectContainer } from '../editor';
import { SelectOption } from '../../../../ui/widgets/select/select';
import { Button } from '../../../../ui/widgets/button/button';
import {
  // FIXME: This is broken cf BiTrash above
  BinSVGIcon as BinIcon,
  PlusIcon
} from '../../../../ui/common/icons';

const fonts = require('../../../../ui/common/fonts.css');
const styles = require('../common.css');

const TextContent = (
  { content }: { content: string | I18nContent | number }, 
  t: TranslateFn,
  maxWidth?: 'Small' | 'Medium' | 'Large') => {
  return (
    <TableCell>
      <span className={classNames(fonts.caption, maxWidth && styles[`maxWidth${maxWidth}`])}>{contentToString(content, t)}</span>
    </TableCell>
  );
};

const I18nTextContent = ({ content }: { content: I18nContent }, t: TranslateFn) => {
  return (
    <TableCell>
      <span className={fonts.caption}>{t(content)}</span>
    </TableCell>
  );
};

interface I18nTextLinkContentProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
}

const I18nTextLinkContent = <T, R>({ field, value }: I18nTextLinkContentProps<T, R>, t: TranslateFn) => {
  if (field.kind === 'i18nTextLink') {
    if (field.onClick !== undefined) {
      const clickFn = field.onClick;
      return (
        <TableCell>
          <span onClick={async () => clickFn(value.id)} className={fonts.link}>
            {t(value[field.name])}
          </span>
        </TableCell>
      );
    }
  }
  return (
    <TableCell>
      <span className={fonts.link}>{t(value[field.name])}</span>
    </TableCell>
  );
};

const DateTimeContent = ({ content }: { content: Instant | null }) => {
  return (
    <TableCell>
      <span className={fonts.caption}>{content !== null ? moment(content).format('YYYY-MM-DD') : ''}</span>
    </TableCell>
  );
};

interface ContentProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
}

const LinkContent = <T, R>({ field, value }: ContentProps<T, R>) => {
  if (field.kind === 'link') {
    if (field.onClick !== undefined) {
      const clickFn = field.onClick;
      return (
        <TableCell>
          <span onClick={async () => clickFn(value.id)} className={fonts.link}>
            {value[field.name]}
          </span>
        </TableCell>
      );
    }
  }
  return (
    <TableCell>
      <span className={fonts.link}>{value[field.name]}</span>
    </TableCell>
  );
};

interface MappedLinkContentProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
  refData?: R;
}

const MappedLinkContent = <E, R>({ field, value, refData }: MappedLinkContentProps<E, R>, t: TranslateFn) => {
  if (field.kind === 'mappedLink') {
    const content = contentToString(field.map(value, refData), t);
    if (field.onClick !== undefined) {
      const clickFn = field.onClick;
      return (
        <TableCell>
          <span onClick={async () => clickFn(value.id)} className={fonts.link}>
            {content}
          </span>
        </TableCell>
      );
    }
  }
  return (
    <TableCell>
      <span className={fonts.link}>{`Invalid field type ${field.kind}`}</span>
    </TableCell>
  );
};

const ImgContent = <T, R>({ field, value }: ContentProps<T, R>) => {
  return (
    <TableCell>
      <img className={styles.imgScaleDown} alt={field.name} src={value[field.name]} />
    </TableCell>
  );
};

const CheckItemContent = ({ content }: { content: boolean }) => {
  return (
    <TableCell>
      <span className={fonts.caption}>
        <CheckItem size="xxsmall" selected={content} inTable disabled={true} />
      </span>
    </TableCell>
  );
};

interface CheckItemActionProps<T, R> {
  field: ListFieldInfo<T, R>;
  content?: ToggleValue<T>;
  value: ListEntry<T>;
  disabled?: boolean;
  onToggle: (fieldName: string) => (itemId: DbKey<T>) => Promise<void>;
}

type TextInputActionProps<T, R> = {
  content?: ItemState<T>;
} & ContentProps<T, R>;

const SelectListContent = ({ content }: { content: SelectOption[] }) => {
  return (
    <TableCell>
      <span className={fonts.caption}>
        {MultiSelectContainer({
          options: content,
          selectedOptions: content,
          xsSpan: 4
        })}
      </span>
    </TableCell>
  );
};

const CheckItemAction = <T, R>({ field, content, value, disabled, onToggle }: CheckItemActionProps<T, R>) => {
  return content
    ? TabularCheckItemObserver({
        field: content.value,
        id: value.id,
        onChange: onToggle(field.name),
        disabled
      })
    : null;
};

const TextInputAction = <T, R>({ field, content, value }: TextInputActionProps<T, R>) => {
  return content
    ? TabularTextInputObserver({
        field: content.value,
        type: 'fieldType' in field ? field.fieldType : 'number',
        placeholder: '000',
        id: value.id
      })
    : null;
};

interface DeleteButtonProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
  onDelete: (item: ListEntry<T>) => void;
}

const DeleteButton = <T, R>({ field, value, onDelete }: DeleteButtonProps<T, R>) => {
  if (onDelete === undefined) return null;
  return (
    <TableCell>
      <div className={styles.buttonListContainer}>
        <div className={styles.buttonList}>
          <Button
            onClick={async () => onDelete(value)}
            Icon={BinIcon} //{BiTrash}
            size="small"
            variant="round"
            disabled={false}
          >
            {field.label}
          </Button>
        </div>
      </div>
    </TableCell>
  );
};

interface OneOffActionButtonProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
  onToggle: (fieldName: string, value: ListEntry<T>, oneOff: boolean) => void;
  isActioned: (id: string) => boolean;
}

/// Once clicked this is meant to update to reflect a state change rendering this component inactive
const OneOffActionButton = <T, R>({ field, value, onToggle, isActioned }: OneOffActionButtonProps<T, R>) => {
  if (onToggle === undefined) return null;
  return (
    <TableCell>
      <div className={styles.buttonListContainer}>
        <div className={styles.buttonList}>
          <Button
            onClick={async () => onToggle(field.name, value, true)}
            Icon={field.name === 'create' ? PlusIcon : undefined}
            size="small"
            variant="round"
            disabled={isActioned(value.id)}
          >
            {!isActioned(value.id) ? field.label : field.name === 'create' ? 'Added' : 'Done'}
          </Button>
        </div>
      </div>
    </TableCell>
  );
};

export interface ListViewCellProps<T, R> {
  field: ListFieldInfo<T, R>;
  value: ListEntry<T>;
  index: number;
  onDelete?: (value: ListEntry<T>) => void;
  onToggle?: (fieldName: string) => (itemId: DbKey<T>) => Promise<void>;
  onOneOffAction?: (fieldName: string, value: ListEntry<T>, oneOff: boolean) => void;
  isActioned?: (id: string) => boolean;
  refData?: R;
  toggleValue?: ToggleValue<T>;
  inputItemState?: ItemState<T>;
  t: TranslateFn;
}

export const ListViewCell = <T, R>({
  field,
  value,
  index,
  onDelete,
  onToggle,
  onOneOffAction,
  isActioned,
  refData,
  toggleValue,
  inputItemState,
  t,
}: ListViewCellProps<T, R>) => {
  let element: JSX.Element | null = null;
  switch (field.kind) {
    case 'text':
      element = TextContent({ content: value[field.name] }, t, field.maxWidth);
      break;
    case 'i18nText':
      element = I18nTextContent({ content: value[field.name] }, t);
      break;
    case 'checkItem':
      element = CheckItemContent({
        content: field.toggle ? field.toggle(value, refData) : value[field.name]
      });
      break;
    case 'mapped':
      element = TextContent({ content: field.map(value, refData) }, t, field.maxWidth);
      break;
    case 'link':
      element = LinkContent({ field, value });
      break;
    case 'mappedLink':
      element = MappedLinkContent({ field, value, refData }, t);
      break;
    case 'i18nTextLink':
      element = I18nTextLinkContent({ field, value }, t);
      break;
    case 'img':
      element = ImgContent({ field, value });
      break;
    case 'instant':
      element = DateTimeContent({ content: value[field.name] });
      break;
    case 'selectList':
      element = SelectListContent({ content: field.map(value, refData) });
      break;

    // These items should track data as it can be updated
    case 'toggle':
      element = onToggle ? CheckItemAction({
        field,
        value,
        content: toggleValue,
        onToggle
      }) : null;
      break;
    case 'action':
      element = TextInputAction({ field, content: inputItemState, value });
      break;
    case 'delete':
      element = onDelete ? DeleteButton({ field, value, onDelete }) : null;
      break;
    case 'action-once':
      element = (onOneOffAction && isActioned) ? OneOffActionButton({
        field,
        value,
        onToggle: onOneOffAction,
        isActioned
      }) : null;
      break;
    default:
      assertNever(field, 'Invalid content type');
      return null;
  }
  return <React.Fragment key={index}>{element}</React.Fragment>;
};

const contentToString = (input: string | I18nContent | number, t: TranslateFn) => {
  let output: string = '(unknown type)';
  if (typeof input === 'string') {
    output = input;
  } else if (typeof input === 'number') {
    output = `${input}`;
  } else if (input.kind === 'text') {
    output = t(input);
  }
  return output;
};



// WEBPACK FOOTER //
// ./src/admin/ui/common/list/list-view-cell.tsx